import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { GALLERY_SECTIONS } from "../data";
import { X, ChevronLeft, ChevronRight, Maximize2, Camera, GraduationCap, FileSignature, FlaskConical, Play, MessageSquareQuote, ImagePlus } from "lucide-react";

const SECTION_ORDER = ["image", "training", "mou", "laboratory", "video", "testimonial"];

const SECTION_ICONS = {
  image: Camera,
  training: GraduationCap,
  mou: FileSignature,
  laboratory: FlaskConical,
  video: Play,
  testimonial: MessageSquareQuote,
};

const PATTERNS = [
  "col-span-2 row-span-2",
  "",
  "row-span-2",
  "",
  "col-span-2",
  "",
  "",
  "row-span-2",
];

export default function Gallery() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("section");
  const [section, setSection] = useState(
    initial && GALLERY_SECTIONS[initial] ? initial : "image"
  );
  const [active, setActive] = useState(null);

  useEffect(() => {
    const q = params.get("section");
    if (q && GALLERY_SECTIONS[q] && q !== section) setSection(q);
    if (!q && section !== "image") setSection("image");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const onPickSection = (key) => {
    setSection(key);
    setActive(null);
    if (key === "image") setParams({});
    else setParams({ section: key });
  };

  const current = GALLERY_SECTIONS[section];
  const items = current.items;

  const close = useCallback(() => setActive(null), []);
  const next = useCallback(
    () => setActive((i) => (i === null ? null : (i + 1) % items.length)),
    [items.length]
  );
  const prev = useCallback(
    () => setActive((i) => (i === null ? null : (i - 1 + items.length) % items.length)),
    [items.length]
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close, next, prev]);

  const totalAcrossSections = useMemo(
    () => Object.values(GALLERY_SECTIONS).reduce((s, v) => s + v.items.length, 0),
    []
  );

  return (
    <div data-testid="gallery-page" className="relative">
      <span className="stamp-num absolute top-6 right-6 md:right-12">N° 03</span>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-10">
        <p className="section-num">
          <strong>03</strong> The gallery
        </p>
        <div className="grid md:grid-cols-12 gap-10 items-end mt-5">
          <h1 className="md:col-span-7 font-display text-display-lg text-ink tracking-tightest fade-up">
            From the
            <br />
            <span className="font-italic text-coral">growing room.</span>
          </h1>
          <p className="md:col-span-5 text-ink/60 max-w-md text-[15px] leading-relaxed fade-up-d1">
            Filter by section — images, training, MoUs, laboratory,
            videos and testimonials.
            <br />
            <span className="text-coral font-medium tabular-nums">
              {totalAcrossSections} photos across {SECTION_ORDER.length} sections
            </span>
          </p>
        </div>

        {/* Section tabs */}
        <div className="mt-12 flex flex-wrap gap-2.5" data-testid="gallery-sections">
          {SECTION_ORDER.map((key) => {
            const sec = GALLERY_SECTIONS[key];
            const Icon = SECTION_ICONS[key];
            const isActive = key === section;
            return (
              <button
                key={key}
                data-testid={`gallery-section-${key}`}
                onClick={() => onPickSection(key)}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border transition-all ${
                  isActive
                    ? "bg-ink text-bone border-ink"
                    : "bg-bone text-ink border-ink/15 hover:border-coral hover:text-coral"
                }`}
              >
                <Icon className="h-4 w-4" />
                {sec.label}
                <span className={`text-[11px] tabular-nums ${isActive ? "text-bone/60" : "text-muted"}`}>
                  {sec.items.length}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Section header */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-6">
        <div className="rounded-3xl bg-sageMist/40 border border-sage/20 px-6 py-5 text-sageDeep flex items-start gap-4">
          {(() => {
            const Icon = SECTION_ICONS[section];
            return <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />;
          })()}
          <div>
            <p className="font-display text-lg leading-tight text-ink tracking-tightest">
              {current.label}
            </p>
            <p className="mt-1 text-sm text-sageDeep/85">{current.note}</p>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-32">
        {items.length === 0 ? (
          <div className="text-center py-24 text-muted">
            <ImagePlus className="h-10 w-10 mx-auto opacity-60" />
            <p className="mt-4">Coming soon — visuals will be added shortly.</p>
          </div>
        ) : section === "training" ? (
          // Posters are tall (~1:2). Use a dedicated grid so the whole poster
          // stays visible — no cropping, framed on a bone backdrop.
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {items.map((g, i) => (
              <button
                key={g.src + i}
                data-testid={`gallery-item-${i}`}
                onClick={() => setActive(i)}
                className="group relative overflow-hidden rounded-4xl bg-boneSoft border border-ink/5 shadow-soft hover:shadow-hoverCard transition-shadow duration-500 text-left"
              >
                <div className="aspect-[1/2] bg-bone p-3 md:p-4">
                  <div className="w-full h-full rounded-3xl overflow-hidden bg-bone">
                    <img
                      src={g.src}
                      alt={g.caption}
                      loading="lazy"
                      className="w-full h-full object-contain transition-transform duration-[1100ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.02]"
                    />
                  </div>
                </div>
                <div className="px-6 pb-6 pt-2 flex items-center justify-between gap-3">
                  <p className="text-ink/85 text-sm font-medium">{g.caption}</p>
                  <span className="h-9 w-9 grid place-items-center rounded-full bg-bone border border-ink/10 text-ink/70 group-hover:bg-coral group-hover:text-white group-hover:border-coral transition-colors">
                    <Maximize2 className="h-4 w-4" />
                  </span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 grid-flow-row-dense auto-rows-[180px] md:auto-rows-[240px] gap-3 md:gap-4">
            {items.map((g, i) => (
              <button
                key={g.src + i}
                data-testid={`gallery-item-${i}`}
                onClick={() => setActive(i)}
                className={`group relative overflow-hidden rounded-4xl bg-sageMist shadow-soft hover:shadow-hoverCard transition-shadow duration-500 ${PATTERNS[i % PATTERNS.length]}`}
              >
                <img
                  src={g.src}
                  alt={g.caption}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.07]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-ink/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-4 left-5 right-12 text-bone text-sm opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                  {g.caption}
                </div>
                <div className="absolute top-3 left-3 h-7 px-2.5 grid place-items-center rounded-full bg-bone/95 text-ink text-[10px] tracking-[0.22em] uppercase font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  {String(i + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                </div>
                <div className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-full bg-bone/95 text-ink opacity-0 group-hover:opacity-100 group-hover:rotate-12 transition-all duration-500">
                  <Maximize2 className="h-4 w-4" />
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox */}
      {active !== null && items[active] && (
        <div
          data-testid="lightbox"
          className="fixed inset-0 z-50 bg-ink/95 flex items-center justify-center p-4 fade-in"
          onClick={close}
        >
          <button
            data-testid="lightbox-close"
            onClick={(e) => { e.stopPropagation(); close(); }}
            className="absolute top-6 right-6 h-12 w-12 grid place-items-center rounded-full bg-bone text-ink hover:bg-coral hover:text-white transition-colors"
            aria-label="Close gallery"
          >
            <X className="h-5 w-5" />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 md:left-10 h-12 w-12 grid place-items-center rounded-full bg-bone/10 hover:bg-bone/20 text-bone transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 md:right-10 h-12 w-12 grid place-items-center rounded-full bg-bone/10 hover:bg-bone/20 text-bone transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          <figure
            className="max-w-5xl w-full max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={items[active].src}
              alt={items[active].caption}
              className="max-h-[78vh] w-auto rounded-xl object-contain shadow-2xl"
            />
            <figcaption className="mt-4 text-bone/80 text-sm tracking-wide font-italic text-lg text-center">
              {items[active].caption}
              <span className="block md:inline text-bone/40 md:ml-3 text-xs tracking-[0.22em] uppercase mt-1 md:mt-0">
                {current.label} · {String(active + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
              </span>
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}
